/**
 * Title — entry point. Two buttons:
 *   - "Plant a New Garden"  (creates a room, random 4-letter code)
 *   - "Join a Garden"       (enter a 4-letter code)
 *
 * Full-bleed mobile-first layout. All positions are viewport-relative.
 */
import Phaser from 'phaser';
import { Audio } from '../systems/AudioManager.js';

export class TitleScene extends Phaser.Scene {
  constructor() { super({ key: 'TitleScene' }); }

  create() {
    this._layout();

    // Respond to viewport changes
    this.scale.on('resize', this._onResize, this);

    this.cameras.main.fadeIn(800, 0, 0, 0);

    // Audio unlock on first tap (browser autoplay policy)
    Audio.attach(this);
    this.input.once('pointerdown', () => {
      if (this.sound.context.state === 'suspended') this.sound.context.resume();
      Audio.playMusic('ambient_garden', { fadeIn: 3000, volume: 0.35 });
    });
  }

  _onResize() {
    // Destroy everything and redraw on resize — simple and reliable
    this.children.removeAll(true);
    this._layout();
  }

  _layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    // === Background — painted twilight garden, cover mode ===
    if (this.textures.exists('garden_twilight')) {
      const bg = this.add.image(w / 2, h / 2, 'garden_twilight').setDepth(0);
      const scale = Math.max(w / bg.width, h / bg.height);
      bg.setScale(scale);
      bg.setAlpha(0.75);
    } else {
      this._drawProcBackdrop(w, h);
    }

    // === Deep vignette bottom for button legibility ===
    const vig = this.add.graphics().setDepth(1);
    const grd = vig.createGeometryMask ? null : null; // phaser doesn't do linear gradients; fake with rects
    // Dark wash top 10%
    vig.fillStyle(0x0a0612, 0.55);
    vig.fillRect(0, 0, w, h * 0.15);
    // Deeper wash bottom 55%
    const steps = 8;
    for (let i = 0; i < steps; i++) {
      const y = h * (0.45 + 0.55 * i / steps);
      const a = 0.08 * (i + 1);
      vig.fillStyle(0x0a0612, a);
      vig.fillRect(0, y, w, h - y);
    }

    // === Drifting dust / fireflies ===
    this._spawnDust(Math.floor(w / 12));

    // === Title block (top third) ===
    const titleSize = Math.min(64, Math.max(40, w * 0.14));
    this.add.text(w / 2, h * 0.22, 'HEIRLOOM', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: `${titleSize}px`,
      fontStyle: '500',
      color: '#f4ecd8',
      stroke: '#1a0e2e',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(10).setLetterSpacing(6);

    this.add.text(w / 2, h * 0.22 + titleSize * 0.75, 'a garden grown together', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: `${Math.max(14, titleSize * 0.28)}px`,
      fontStyle: 'italic',
      color: '#d4b08a',
    }).setOrigin(0.5).setDepth(10).setAlpha(0.9);

    // === Buttons (bottom half, stacked) ===
    const btnW = Math.min(w * 0.82, 380);
    const btnH = Math.max(62, Math.min(78, h * 0.085));
    const btnGap = btnH * 0.32;
    const btnCenterY = h * 0.68;

    this._createButton(w / 2, btnCenterY - (btnH + btnGap) / 2, btnW, btnH, 'Plant a New Garden', () => this._startNewRoom());
    this._createButton(w / 2, btnCenterY + (btnH + btnGap) / 2, btnW, btnH, 'Join a Garden', () => this._showJoinPrompt());

    // Footer
    this.add.text(w / 2, h - 28, 'made for two', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '12px',
      fontStyle: 'italic',
      color: '#6a5a4a',
    }).setOrigin(0.5).setDepth(10);
  }

  _drawProcBackdrop(w, h) {
    const g = this.add.graphics().setDepth(0);
    g.fillGradientStyle(0x0a0612, 0x0a0612, 0x3a2a4a, 0x1a0e2e, 1, 1, 1, 1);
    g.fillRect(0, 0, w, h);
    // Moon
    this.add.circle(w * 0.75, h * 0.18, 40, 0xf4ecd8, 0.9).setDepth(1);
    this.add.circle(w * 0.75, h * 0.18, 60, 0xf4ecd8, 0.12).setDepth(1);
  }

  _spawnDust(count) {
    const w = this.scale.width;
    const h = this.scale.height;
    for (let i = 0; i < count; i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const dust = this.add.circle(x, y, 0.8 + Math.random() * 1.5, 0xf4ecd8, 0.4 + Math.random() * 0.4).setDepth(3);
      this.tweens.add({
        targets: dust,
        y: y - 30 - Math.random() * 60,
        x: x + (Math.random() - 0.5) * 40,
        alpha: { from: dust.alpha, to: 0 },
        duration: 6000 + Math.random() * 4000,
        repeat: -1,
        onRepeat: () => {
          dust.x = Math.random() * this.scale.width;
          dust.y = this.scale.height + 10;
          dust.alpha = 0.4 + Math.random() * 0.4;
        },
      });
    }
  }

  _createButton(x, y, btnW, btnH, label, onClick) {
    const bg = this.add.graphics().setDepth(10);
    const draw = (fill, stroke, alpha = 0.88) => {
      bg.clear();
      bg.fillStyle(fill, alpha);
      bg.fillRoundedRect(x - btnW / 2, y - btnH / 2, btnW, btnH, 14);
      bg.lineStyle(1.5, stroke, 0.9);
      bg.strokeRoundedRect(x - btnW / 2, y - btnH / 2, btnW, btnH, 14);
    };
    draw(0x1a0e2e, 0xd4b08a);

    const textSize = Math.max(22, Math.min(28, btnH * 0.38));
    const text = this.add.text(x, y, label, {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: `${textSize}px`,
      fontStyle: '500',
      color: '#f4ecd8',
    }).setOrigin(0.5).setDepth(11);

    const zone = this.add.rectangle(x, y, btnW, btnH, 0x000000, 0).setDepth(12).setInteractive({ useHandCursor: true });
    zone.on('pointerover', () => { draw(0x2a1a44, 0xf4ecd8, 0.95); text.setColor('#ffd700'); });
    zone.on('pointerout', () => { draw(0x1a0e2e, 0xd4b08a); text.setColor('#f4ecd8'); });
    zone.on('pointerdown', () => {
      this.tweens.add({ targets: text, scale: 0.95, duration: 80, yoyo: true });
      onClick();
    });
    return { bg, text, zone };
  }

  // ── Room flow ───────────────────────────────────────────────

  _startNewRoom() {
    const code = this._generateCode();
    this._joinRoom(code, 'host');
  }

  _showJoinPrompt() { this._buildCodeInput(); }

  _buildCodeInput() {
    const w = this.scale.width;
    const h = this.scale.height;
    if (this._modal) return;
    this._modal = [];

    const dim = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.85).setDepth(50).setInteractive();
    this._modal.push(dim);

    const pW = Math.min(w * 0.9, 420);
    const pH = Math.min(h * 0.7, 460);
    const panel = this.add.graphics().setDepth(51);
    panel.fillStyle(0x1a0e2e, 0.98);
    panel.fillRoundedRect(w / 2 - pW / 2, h / 2 - pH / 2, pW, pH, 16);
    panel.lineStyle(2, 0xd4b08a, 0.9);
    panel.strokeRoundedRect(w / 2 - pW / 2, h / 2 - pH / 2, pW, pH, 16);
    this._modal.push(panel);

    const title = this.add.text(w / 2, h / 2 - pH / 2 + 34, 'Enter room code', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '24px',
      color: '#f4ecd8',
    }).setOrigin(0.5).setDepth(52);
    this._modal.push(title);

    // Code slots
    let code = '';
    const slots = [];
    const slotW = 48, slotH = 58, gap = 10;
    const totalW = slotW * 4 + gap * 3;
    const startX = w / 2 - totalW / 2 + slotW / 2;
    const slotY = h / 2 - pH / 2 + 100;
    for (let i = 0; i < 4; i++) {
      const sx = startX + i * (slotW + gap);
      const slotBg = this.add.graphics().setDepth(52);
      slotBg.fillStyle(0x0a0612, 0.9);
      slotBg.fillRoundedRect(sx - slotW / 2, slotY - slotH / 2, slotW, slotH, 8);
      slotBg.lineStyle(1.5, 0xd4b08a, 0.7);
      slotBg.strokeRoundedRect(sx - slotW / 2, slotY - slotH / 2, slotW, slotH, 8);
      const letterText = this.add.text(sx, slotY, '', {
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: '34px',
        color: '#f4ecd8',
      }).setOrigin(0.5).setDepth(53);
      slots.push({ text: letterText });
      this._modal.push(slotBg, letterText);
    }

    // On-screen keyboard
    const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ'.split(''); // no I, O
    const cols = 6;
    const keyW = Math.min(42, (pW - 60) / cols - 4);
    const keyH = keyW * 0.92;
    const keyGap = 4;
    const kbStartY = h / 2 - pH / 2 + 180;
    const kbStartX = w / 2 - ((cols * (keyW + keyGap)) - keyGap) / 2 + keyW / 2;
    letters.forEach((L, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const kx = kbStartX + col * (keyW + keyGap);
      const ky = kbStartY + row * (keyH + keyGap);
      const kbg = this.add.graphics().setDepth(52);
      kbg.fillStyle(0x2a1a44, 0.9);
      kbg.fillRoundedRect(kx - keyW / 2, ky - keyH / 2, keyW, keyH, 6);
      kbg.lineStyle(1, 0xd4b08a, 0.5);
      kbg.strokeRoundedRect(kx - keyW / 2, ky - keyH / 2, keyW, keyH, 6);
      const ktxt = this.add.text(kx, ky, L, {
        fontFamily: 'Cormorant Garamond, serif',
        fontSize: `${Math.floor(keyH * 0.55)}px`,
        color: '#f4ecd8',
      }).setOrigin(0.5).setDepth(53);
      const khit = this.add.rectangle(kx, ky, keyW, keyH, 0x000000, 0).setDepth(54).setInteractive({ useHandCursor: true });
      khit.on('pointerdown', () => {
        if (code.length < 4) {
          code += L;
          slots[code.length - 1].text.setText(L);
          if (code.length === 4) this.time.delayedCall(220, () => this._joinRoom(code, 'guest'));
        }
      });
      this._modal.push(kbg, ktxt, khit);
    });

    const closeBtn = this.add.text(w / 2 + pW / 2 - 24, h / 2 - pH / 2 + 22, '×', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '32px', color: '#6a5a4a',
    }).setOrigin(0.5).setDepth(55).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this._closeModal());
    closeBtn.on('pointerover', () => closeBtn.setColor('#d4b08a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#6a5a4a'));
    this._modal.push(closeBtn);
  }

  _closeModal() {
    if (!this._modal) return;
    this._modal.forEach(o => o.destroy());
    this._modal = null;
  }

  _generateCode() {
    const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    let code = '';
    for (let i = 0; i < 4; i++) code += letters[Math.floor(Math.random() * letters.length)];
    return code;
  }

  _joinRoom(code, role) {
    this._closeModal();
    this.scale.off('resize', this._onResize, this);
    this.cameras.main.fadeOut(600, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('GardenScene', { roomCode: code, role });
    });
  }
}
